/** Inline card entry and SetupIntent confirmation using Stripe Elements. */
import * as Sentry from '@sentry/react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useEffect, useState } from 'react'

import { REGIONS_BY_ID } from '@snootsvet/shared'
import type { PostPortalSetupIntentResponse } from '@snootsvet/shared/portal'

import { Button } from '@/customer/components/ui/button'
import { stripeForRegion } from '@/customer/lib/stripe'

interface AddPaymentMethodFormProps {
  /**
   * The SetupIntent exactly as `POST /portal/billing/setup-intents`
   * returned it - secret and minting region together, deliberately
   * not two props.
   *
   * A secret can only be confirmed by the Stripe account that
   * created it, and a mismatch fails silently until the member
   * submits their card. Passing the whole response makes that
   * pairing structural.
   */
  setupIntent: PostPortalSetupIntentResponse
  /** Called after a card is successfully saved. */
  onSuccess: () => void
  /** Called when the member cancels. */
  onCancel: () => void
}

export function AddPaymentMethodForm({
  setupIntent,
  onSuccess,
  onCancel,
}: AddPaymentMethodFormProps) {
  const { clientSecret, region } = setupIntent
  const stripe = stripeForRegion(region)

  // A missing key is a deploy mistake, not a member error, so report
  // it rather than relying on someone noticing the panel below.
  useEffect(() => {
    if (stripe) return
    Sentry.captureMessage(
      `Stripe publishable key missing for region "${region}" - card entry disabled`,
      'error'
    )
  }, [stripe, region])

  if (!stripe) {
    const regionName = REGIONS_BY_ID[region]?.name
    return (
      <p className="rounded-lg border border-dashed p-4 text-center text-sm text-destructive">
        Card entry isn&apos;t available for {regionName ? `the ${regionName}` : 'this region'} right
        now. Please message us and we&apos;ll help you finish signing up.
      </p>
    )
  }

  return (
    <Elements stripe={stripe} options={{ clientSecret }}>
      <CardEntryFields onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  )
}

function CardEntryFields({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setIsSubmitting(true)
    setError(null)

    const result = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    })

    setIsSubmitting(false)
    if (result.error) {
      setError(result.error.message ?? 'Something went wrong')
    } else {
      onSuccess()
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border border-dashed p-4">
      {/*
        Wallets are not payment method types, so the SetupIntent's
        `payment_method_types: ['card']` doesn't reach them - Link,
        Google Pay and Apple Pay all ride on top of `card` and default
        to 'auto'. Turning them off has to happen here, client-side.
      */}
      <PaymentElement
        options={{ wallets: { link: 'never', googlePay: 'never', applePay: 'never' } }}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          className="text-destructive"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save card'}
        </Button>
      </div>
    </form>
  )
}
